import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuthLogin } from '@hooks/security/use-auth-login';
import { getReturnUrl, withReturnUrl } from '@/security/session';
import { useLocalize } from '@hooks/use-localize';
import { authLoginSchema } from '@views/security/auth-login.schema';
import type { IAuthLoginFormValues, IAuthLoginModel, IAuthLoginSubmitResult } from '@/types/security/auth-login';
import FieldError from '@components/field-error';

const AuthLogin = () => {
    const { model, isLoading, error, submit, isSubmitting, submitError } = useAuthLogin();

    if (isLoading) {
        return (
            <div className='row'>
                <div className='col-xs-12'>Loading&hellip;</div>
            </div>
        );
    }

    return (
        <>
            {error && (
                <div className='row'>
                    <div className='col-xs-12'>
                        <div className='alert alert-danger'>{error}</div>
                    </div>
                </div>
            )}
            <AuthLoginContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </>
    );
};

interface IAuthLoginContentProps {
    model: IAuthLoginModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IAuthLoginFormValues) => Promise<IAuthLoginSubmitResult>;
}

const AuthLoginContent = ({ model, isSubmitting, submitError, onSubmit }: IAuthLoginContentProps) => {
    console.log(model.languageData)
    const t = useLocalize(model.languageData);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const {
        register,
        handleSubmit,
        watch,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<IAuthLoginFormValues>({
        resolver: zodResolver(authLoginSchema),
        defaultValues: { isMobile: '0', email: '', mobileNumber: '' },
    });

    const isMobile = watch('isMobile') === '1';

    useEffect(() => {
        clearErrors();
    }, [isMobile, clearErrors]);

    const onValid = async (values: IAuthLoginFormValues) => {
        const result = await onSubmit(values);

        // Surface server-side field errors through the same channel as client validation.
        if (!result.ok && result.errors) {
            Object.entries(result.errors).forEach(([field, message]) => {
                setError(field as keyof IAuthLoginFormValues, { type: 'server', message });
            });
        }

        if (result.ok) {
            navigate(withReturnUrl('/register/auth-validate/' + result.token, getReturnUrl(searchParams)));
        }
    };

    return (
        <div className='container'>
            <div className='row'>
                <div className='col-xs-12'>
                    <div className='alert alert-info' style={{ fontSize: 16 }}>
                        <p style={{ marginBottom: 0 }}>
                            {t(
                                'Dash_AuthLogin_Intro',
                                'To continue where you left off, please enter either the email or U.S. mobile number that you registered with, and we will send a verification code to the specified destination that you can use to login.',
                            )}
                        </p>
                    </div>

                    {submitError && <div className='alert alert-danger'>{submitError}</div>}

                    <form id='RegistrationForm' className='form' onSubmit={handleSubmit(onValid)}>
                        <div className='form-group'>
                            <div className='radio'>
                                <label>
                                    <input
                                        type='radio'
                                        id='IsMobile_false'
                                        value='0'
                                        {...register('isMobile')}
                                    />{' '}
                                    {t('Dash_AuthLogin_SendCodeToEmail', 'Send code to my email')}
                                </label>
                            </div>
                            <div className='radio'>
                                <label>
                                    <input
                                        type='radio'
                                        id='IsMobile_true'
                                        value='1'
                                        {...register('isMobile')}
                                    />{' '}
                                    {t('Dash_AuthLogin_SendCodeToMobile', 'Send code to my mobile phone')}
                                </label>
                            </div>
                        </div>

                        {!isMobile && (
                            <div id='emailGroup' className='form-group'>
                                <label htmlFor='Email'>
                                    <FieldError message={errors.email?.message} />{' '}
                                    {t('DashLabel_Email', 'Email')}
                                </label>
                                <input
                                    id='Email'
                                    className='form-control'
                                    maxLength={64}
                                    {...register('email')}
                                />
                            </div>
                        )}

                        {isMobile && (
                            <div id='phoneGroup' className='form-group'>
                                <label htmlFor='MobileNumber'>
                                    <FieldError message={errors.mobileNumber?.message} />{' '}
                                    {t('DashLabel_USMobilePhone', 'U.S. Mobile Phone')}
                                </label>
                                <div className='input-group'>
                                    <span className='input-group-addon'>+1</span>
                                    <input
                                        id='MobileNumber'
                                        className='form-control'
                                        maxLength={10}
                                        {...register('mobileNumber')}
                                    />
                                </div>
                            </div>
                        )}

                        <div style={{ marginTop: 16 }}>
                            <button
                                type='submit'
                                className='btn btn-primary btn-lg'
                                style={{ marginTop: 20 }}
                                disabled={isSubmitting}
                            >
                                {t('Dash_AuthLogin_SubmitButton', 'Send Code')}
                            </button>
                        </div>
                    </form>

                    <div className='col-xs-12 col-sm-3 col-sm-offset-1'></div>
                </div>
            </div>
        </div>
    );
};

export default AuthLogin;
